import {
    HorizAlignment,
    ISearch,
    ISearchProfile,
    ISearchProvider,
    ISearchProviderField,
    ISearchRequestSubset,
    SearchProviderFieldType,
} from "../PA.UIM.Core/PA.UIM.Core.IF.Search";
import { SearchUtils } from "../PA.UIM.Core/SearchUtils";
import { PASearch } from "../PA.Core/PASearch";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import * as _ from "lodash";

const vUI_Vol_Decomp_Search_Fields: { [id: string]: ISearchProviderField } = {
    WB_VOL_DECOMP_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: "Wb Id",
            columnResourceName: "colwbId",
            alignment: HorizAlignment.Left,
        },
        domainOptions: {},
    },
    VOL_DECOMP_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Id",
            columnResourceName: "colId",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { maxLength: 50 },
    },
    VOL_DECOMP_NAME: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Name",
            columnResourceName: "colName",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { maxLength: 256 },
    },
    COMMENTS: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Comments",
            columnResourceName: "colTreePath",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { maxLength: 1333 },
    },
    MDSET_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Master Data Set Id",
            columnResourceName: "colId",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { maxLength: 50 },
    },
    MDSET_NAME: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Master Data Set",
            columnResourceName: "colMDSetName",
            alignment: HorizAlignment.Left,
        },
        domainOptions: {
            profileId: "default",
            bUseSearchProvidersDistinctValues: true,
            maxLength: 256,
        },
    },
    MODIFIED_DTTS: {
        type: SearchProviderFieldType.sapdate,
        renderOptions: {
            columnName: "Modified Date",
            columnResourceName: "colModifiedDt",
            alignment: HorizAlignment.Center,
            dateFormat: "MM-dd-yy",
        },
        domainOptions: {},
    },
    CREATED_DTTS: {
        type: SearchProviderFieldType.sapdate,
        renderOptions: {
            columnName: "Created Date",
            columnResourceName: "colCreatedDt",
            alignment: HorizAlignment.Center,
            dateFormat: "MM-dd-yy",
        },
        domainOptions: {},
    },
    WB_MDSET_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: "Master Data Set WB Id",
            columnResourceName: "colMDSetId",
            alignment: HorizAlignment.Center,
        },
        domainOptions: {},
    },
    WB_SALESORG_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: "wb Sales Org",
            columnResourceName: "colSalesOrgId",
            alignment: HorizAlignment.Left,
        },
        domainOptions: {},
    },
    SALESORG_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "SalesOrg Id",
            columnResourceName: "colSalesOrgId",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { profileId: "default", bUseSearchProvidersDistinctValues: true, maxLength: 50 },
    },
    SALESORG_NAME: {
        type: SearchProviderFieldType.string,
        renderOptions: {
            columnName: "Sales Org",
            columnResourceName: "colSalesOrgName",
            alignment: HorizAlignment.Left,
        },
        domainOptions: { profileId: "default", bUseSearchProvidersDistinctValues: true, maxLength: 256 },
    },
};

const VolDecompSearchProviderBasic: ISearchProvider = {
    keyFields: {
        id: "WB_VOL_DECOMP_ID",
        id_ui: "VOL_DECOMP_ID",
        name: "VOL_DECOMP_NAME",
    },

    searchProviderFields: vUI_Vol_Decomp_Search_Fields,

    search(searchRequestSubset: ISearchRequestSubset): Promise<any[]> {
        return new Promise<any[]>((resolve, error) => {
            const findRequest = SearchUtils.makeSearchRequest(
                searchRequestSubset,
                "vUI_Vol_Decomp_Search",
                this.searchProviderFields,
                [],
            );

            const usrContext = PAEnvironment.getValue(PAEnvironment.UserContext);
            if (!usrContext.hasRoleId("VIEW_ALL")) {
                searchRequestSubset.where["WB_MDSET_ID"] = { inq: _.map(usrContext.WB_MasterDataSets, "WB_MDSET_ID") };
            }

            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    resolve(res);
                })
                .catch((err) => {
                    error(err);
                });
        });
    },

    // Filter with drop down in the TPM Event Search
    distinctValues(fieldName: string): Promise<string[] | number[]> {
        return new Promise<string[] | number[]>((resolve, error) => {
            const findRequest = SearchUtils.makeSearchRequest({}, "vUI_Vol_Decomp_Search", this.searchProviderFields, [
                fieldName,
            ]);

            const usrContext = PAEnvironment.getValue(PAEnvironment.UserContext);
            if (!usrContext.hasRoleId("VIEW_ALL")) {
                findRequest.where["WB_MDSET_ID"] = { inq: _.map(usrContext.WB_MasterDataSets, "WB_MDSET_ID") };
            }

            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    resolve(
                        _.map(res, (aRow) => {
                            return aRow[fieldName];
                        }),
                    );
                })
                .catch((err) => {
                    error(err);
                });
        });
    },
};

const VolDecompSearchProfileByName: ISearchProfile = {
    profileId: "default",
    profileName: "Search by Id, Name",
    renderOptions: { ui5SourceId: "ControlPointSearchProfileByName" },

    profileFilter: [
        { profileFilterName: "Id", searchProviderFieldId: "VOL_DECOMP_ID", renderOptions: {} },
        { profileFilterName: "Name", searchProviderFieldId: "VOL_DECOMP_NAME", renderOptions: {} },
        {
            profileFilterName: "Master Data Set",
            searchProviderFieldId: "MDSET_NAME",
            bMultiSelect: true,
            renderOptions: {},
        },
        {
            profileFilterName: "Sales Org",
            searchProviderFieldId: "SALESORG_NAME",
            bMultiSelect: true,
            renderOptions: {},
        },
    ], //ISearchFields
    profileDisplayFields: [
        "VOL_DECOMP_ID",
        "VOL_DECOMP_NAME",
        "MDSET_NAME",
        "SALESORG_NAME",
        "CREATED_DTTS",
        "MODIFIED_DTTS",
    ],

    profileSearchProvider: VolDecompSearchProviderBasic,

    profileSortIdDefault: "", //string;
    profileSorts: [], //ISearchSort[];
};

const VolDecompSearch: ISearch = {
    searchName: "Find Volume Decomp Control Point",
    renderOptions: { ui5ResourceId: "LESpendControlPointSearch" },
    searchProfileIdDefault: "default",
    searchProfiles: [VolDecompSearchProfileByName],
};

PAEnvironment.registerBusinessObjectSearch("VolDecompBO", "VolDecomp", VolDecompSearch, "Volume Decomp Search");

export { VolDecompSearch };
